import { toVehicleReference } from "./to-vehicle-reference";
import type { VehicleDetailDto, VehicleDto } from "@/lib/api";
import type { RentalType, Vehicle } from "../../types/vehicle.types";
import { toOwner } from "@/utils/mappers/to-owner";
import { cleanWebApiDateString } from "@/utils/date.utilities";

/** Maps the list-shaped vehicle payload returned by the search/list endpoints. */
export function toVehicle(dto: VehicleDto): Vehicle {
	return {
		id: dto.vehicle?.id,
		name: dto.vehicle?.name,
		licensePlate: dto.vehicle?.licensePlate,
		vehicle: dto.vehicle ? toVehicleReference(dto.vehicle) : undefined,
		batteryStatus: dto.batteryStatus,
		homePosition: dto.homePosition,
		odometer: dto.odometer,
		owner: toOwner(dto.owner),
		infoBeforeUse: dto.infoBeforeUse,
		infoAfterUse: dto.infoAfterUse,
		infoGeneral: dto.infoGeneral,
		manualLink: dto.manualLink,
	};
}

/**
 * Maps the detail-shaped payload from `GET /fleet/vehicle/v1/vehicles/{id}`.
 *
 * Every branch below is optional-chained on purpose: the fleet DTO marks
 * `specifications` and each of its members nullable, so a vehicle with no
 * specification record must render blank rather than crash.
 */
export function toVehicleDetail(dto: VehicleDetailDto): Vehicle {
	const specifications = dto.specifications;

	return {
		...toVehicle(dto),
		address: dto.homePosition?.address,
		lastAddress: dto.lastPosition?.address,
		lastPosition: dto.lastPosition,
		description: dto.description,
		seats: dto.seats,
		hourPrice: dto.hourPrice,
		dayPrice: dto.dayPrice,
		kilometerPrice: dto.kilometerPrice,
		rentalType: dto.rentalType as RentalType | undefined,
		electricRangeKm: dto.electricRange,
		operationalStatus: dto.operationalStatus,
		permissions: dto.permissions,
		intentIds: dto.intentIds,
		equipmentIds: dto.equipmentIds,
		alwaysAvailable: dto.alwaysAvailable,
		minimumDriverAge: specifications?.minimumDriverAge,
		doors: specifications?.doors,
		color: specifications?.exteriorColor?.name,
		// The booking engine sends ZonedDateTimes with a `[UTC]` suffix.
		expectedInFleetDate: cleanWebApiDateString(dto.expectedInFleetDate),
		actualInFleetDate: cleanWebApiDateString(dto.actualInFleetDate),
		expectedOutOfFleetDate: cleanWebApiDateString(dto.expectedOutOfFleetDate),
		actualOutOfFleetDate: cleanWebApiDateString(dto.actualOutOfFleetDate),
		vehicleType: dto.vehicleType
			? { id: dto.vehicleType.id, name: dto.vehicleType.name }
			: undefined,
		details: specifications
			? {
					model: specifications.model,
					// The fleet spec names these id/name; the domain keeps enumId/
					// description because that is the shape the reference-list
					// pickers and the edit form bind to.
					bodyStyle: {
						enumId: specifications.bodyStyle?.id,
						description: specifications.bodyStyle?.name,
					},
					exteriorColor: {
						enumId: specifications.exteriorColor?.id,
						description: specifications.exteriorColor?.name,
					},
					doors: specifications.doors,
					rimSize: specifications.rimSize,
					luggageCapacity: specifications.luggageCapacity,
					batteryCapacityKWh: specifications.batteryCapacityKWh,
					chargeSpeedAC: specifications.chargeSpeedAC,
					chargeSpeedDC: specifications.chargeSpeedDC,
					powerKW: specifications.powerKW,
					averageConsumption: specifications.averageConsumption,
					minimumDriverAge: specifications.minimumDriverAge,
					minimumYearsOfDriverLicense:
						specifications.minimumYearsOfDriverLicense,
					minimumRentalHours: specifications.minimumRentalHours,
					preparationTimeMinutes: specifications.preparationTimeMinutes,
					onboardingTimeMinutes: specifications.onboardingTimeMinutes,
				}
			: undefined,
	};
}
